import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { Layer as DeckGlLayer } from "@deck.gl/core";
import { useMachine, useSelector } from "@xstate/react";

import { Dimension, DimensionValue, DimensionValueSet } from "@/lib/dimensions";

import { mapStateMachine, MapStateMachineInput } from "./map-state-machine";
import { LayerSetting, LayerSettingValue, LayerSettingValueSet } from "./layer-settings";

export type MapLayersController = {
  dimensions: Dimension[];
  currentDimensionValues: DimensionValueSet;
  setDimensionValue: (dimensionId: string, value: DimensionValue) => void;

  isLoading: boolean;
  isPlaying: boolean;
  togglePlaying: () => void;
  playInLoopEnabled: boolean;
  togglePlayInLoop: () => void;

  refresh: () => Promise<void>;
  recalculateDimensions: () => void;

  layers: MapLayerController[];
};

export type MapLayerController = {
  id: string;
  title: string;
  dimensions: Dimension[] | undefined;

  settings: LayerSetting[];
  currentSettingValues: LayerSettingValueSet;
  setSettingValue: (settingId: string, settingValue: LayerSettingValue<unknown>) => void;

  isEnabled: boolean;
  isLoading: boolean;
  isReady: boolean;
  hasError: boolean;
  hasPartialLoadErrors: boolean;
  hasLoadErrorAtCurrentTime: boolean;
  isActive: boolean;
  isForecast: boolean;

  renderedLayer: DeckGlLayer | undefined;
  renderedLegend: ReactNode | undefined;
  renderedComponents: ReactNode | undefined;

  toggle: () => void;
  setIsEnabled: (value: boolean) => void;
};

export function useMapLayers(input: MapStateMachineInput): MapLayersController {
  const [state, send, mapActorRef] = useMachine(mapStateMachine, { input });
  const currentDimensionValues = state.context.currentDimensionValues;

  useEffect(() => {
    send({ type: "SET_TIME_RANGE", timeRange: input.timeRange });
  }, [input.timeRange]);

  return {
    dimensions: state.context.dimensions,
    currentDimensionValues,
    setDimensionValue: useCallback(
      (dimensionId: string, value: DimensionValue) => {
        send({ type: "SET_DIMENSION_VALUE", dimensionId, value });
      },
      [send],
    ),

    isLoading: !state.matches("ready"),

    isPlaying: state.matches({ ready: "playing" }),
    togglePlaying: useCallback(() => send({ type: "TOGGLE_PLAYING" }), [send]),

    playInLoopEnabled: state.context.canLoop,
    togglePlayInLoop: useCallback(() => send({ type: "TOGGLE_PLAY_IN_LOOP" }), [send]),

    refresh: useCallback(async () => {
      send({ type: "REFRESH" });
      await new Promise<void>((resolve) => {
        const subscription = mapActorRef.subscribe((newState) => {
          if (newState.matches("ready")) {
            subscription.unsubscribe();
            resolve();
          }
        });
      });
    }, [send]),

    recalculateDimensions: useCallback(() => {
      send({ type: "RECALCULATE_DIMENSIONS" });
    }, [send]),

    // FIXME: this violates the rule "only call hooks at the top level"
    // See: https://legacy.reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level
    // If layerMachineRefs ever changes the hooks will get out of sync causing bugs
    layers: state.context.layerMachineRefs.map((layerActorRef) => {
      const layer = useSelector(layerActorRef, (snapshot) => snapshot.context.layer);
      const currentSettingValues = useSelector(
        layerActorRef,
        (snapshot) => snapshot.context.currentSettingValues,
      );
      const isEnabled = useSelector(layerActorRef, (snapshot) =>
        snapshot.matches("enabled"),
      );

      return {
        id: layerActorRef.id,
        title: useMemo(
          () => layer.getTitle(currentDimensionValues),
          [currentDimensionValues, layer],
        ),
        dimensions: layer.dimensions,

        settings: layer.settings,
        currentSettingValues,
        setSettingValue: useCallback(
          (settingId: string, settingValue: LayerSettingValue<unknown>) => {
            layerActorRef.send({ type: "SET_SETTING_VALUE", settingId, settingValue });
          },
          [layerActorRef],
        ),

        isEnabled,
        isLoading: useSelector(
          layerActorRef,
          (snapshot) =>
            snapshot.matches({ enabled: "initializing" }) ||
            snapshot.matches({ enabled: { ready: "reinitializing" } }) ||
            snapshot.matches({ enabled: { ready: "loading" } }),
        ),
        isReady: useSelector(layerActorRef, (snapshot) =>
          snapshot.matches({ enabled: "ready" }),
        ),
        hasError: useSelector(layerActorRef, (snapshot) =>
          snapshot.matches({ enabled: "error" }),
        ),
        hasPartialLoadErrors: useMemo(() => layer.hasPartialLoadErrors(), [layer]),
        hasLoadErrorAtCurrentTime: useMemo(
          () => layer.hasLoadError(currentDimensionValues),
          [currentDimensionValues, layer],
        ),
        isActive: useMemo(
          () => layer.isActive(currentDimensionValues),
          [currentDimensionValues, layer],
        ),
        isForecast: useMemo(
          () => layer.isForecast(currentDimensionValues),
          [currentDimensionValues, layer],
        ),

        renderedLegend: useMemo(() => {
          return layer.renderLegend(currentDimensionValues, currentSettingValues);
        }, [layer, currentDimensionValues, currentSettingValues]),

        renderedLayer: useMemo(() => {
          if (!isEnabled) return;
          return layer.renderLayer(currentDimensionValues, currentSettingValues);
        }, [layer, isEnabled, currentDimensionValues, currentSettingValues]),

        renderedComponents: useMemo(() => {
          if (!isEnabled) return;
          return layer.renderComponents(currentDimensionValues, currentSettingValues);
        }, [layer, isEnabled, currentDimensionValues, currentSettingValues]),

        toggle: useCallback(() => layerActorRef.send({ type: "TOGGLE" }), [layerActorRef]),
        setIsEnabled: useCallback(
          (isEnabled: boolean) => {
            if (isEnabled) {
              layerActorRef.send({ type: "ENABLE" });
            } else {
              layerActorRef.send({ type: "DISABLE" });
            }
          },
          [layerActorRef],
        ),
      };
    }),
  };
}
